import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import {
  Button,
  DateTimePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@zuruflex/ui';
import { AlarmClock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Putting a thread down until a time worth picking it back up.
 *
 * `SNOOZED` used to be kept off the status control on purpose: the API
 * refuses it without a `snoozedUntil`, there was nothing here that asked
 * for one, and nothing woke a snoozed thread afterwards — so a thread put
 * down stayed down. `POST /v1/threads/sweep` is what changed that. It
 * wakes what is due and runs on cron, so a snooze is now a promise the
 * platform keeps rather than a hole to drop work into.
 *
 * It is still not on the status `Select`, and that is the other half of
 * the old reasoning standing: a status you can pick without saying when
 * is a request the API will refuse. The date and the status leave here
 * together, always, which is the only shape `PATCH /v1/threads/:id`
 * accepts.
 */

/** 09:00 — the start of the working day, not midnight on a desk nobody is at. */
const WORKING_HOUR = 9;

function at(date: Date, hour: number): Date {
  const next = new Date(date);
  next.setHours(hour, 0, 0, 0);
  return next;
}

function presetsFor(now: Date): Date[] {
  const laterToday = new Date(now.getTime() + 3 * 60 * 60_000);
  laterToday.setMinutes(0, 0, 0);

  const tomorrow = at(new Date(now.getTime() + 86_400_000), WORKING_HOUR);

  // The next Monday that is not today — "Monday" said on a Monday means
  // the one coming, not the one you are standing in.
  const monday = at(now, WORKING_HOUR);
  monday.setDate(monday.getDate() + ((8 - monday.getDay()) % 7 || 7));

  const nextWeek = at(new Date(now.getTime() + 7 * 86_400_000), WORKING_HOUR);

  // Deduplicated and ordered, because Monday and "next week" are the same
  // instant on a Monday and two identical rows is a menu that looks broken.
  const seen = new Set<number>();
  return [laterToday, tomorrow, monday, nextWeek]
    .filter((date) => date.getTime() > now.getTime())
    .sort((a, b) => a.getTime() - b.getTime())
    .filter((date) => {
      if (seen.has(date.getTime())) return false;
      seen.add(date.getTime());
      return true;
    });
}

export interface SnoozeMenuProps {
  /** Always sent with `status: 'SNOOZED'` by the caller — both or neither. */
  onSnooze: (until: Date) => void;
  onWake: () => void;
  snoozedUntil: string | null;
  disabled?: boolean;
}

export function SnoozeMenu({ onSnooze, onWake, snoozedUntil, disabled }: SnoozeMenuProps) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const [picking, setPicking] = useState(false);
  const [custom, setCustom] = useState<Date | undefined>();

  /*
   * Computed at open rather than held in state: "in three hours" read off
   * a mount from this morning is not three hours from now, and a desk is
   * left open all day.
   */
  const presets = presetsFor(new Date());

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="w-full" disabled={disabled}>
            <AlarmClock className="size-4" aria-hidden />
            {snoozedUntil
              ? t('inbox.snoozeSet', { date: formatDateTimeIn(snoozedUntil, locale) })
              : t('inbox.snooze')}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>{t('inbox.snoozeUntil')}</DropdownMenuLabel>
          {/*
            The preset's label is the instant itself, formatted in the
            reader's locale. "Later today" and "Monday 9am" would be four
            new strings in three languages saying what the date already
            says — and a date is the thing an agent is actually agreeing to.
          */}
          {presets.map((date) => (
            <DropdownMenuItem key={date.getTime()} onSelect={() => onSnooze(date)}>
              {formatDateTimeIn(date, locale)}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setPicking(true)}>
            {t('inbox.snoozeUntil')}…
          </DropdownMenuItem>
          {snoozedUntil ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onWake}>{t('inbox.snoozeCancel')}</DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={picking} onOpenChange={setPicking}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('inbox.snoozeUntil')}</DialogTitle>
            <DialogDescription>{t('inbox.snooze')}</DialogDescription>
          </DialogHeader>

          <DateTimePicker
            value={custom}
            onChange={setCustom}
            disablePast
            labels={{ time: t('common:field.time'), done: t('common:action.ok') }}
            placeholder={t('inbox.snoozeUntil')}
          />

          <DialogFooter>
            <Button variant="ghost" onClick={() => setPicking(false)}>
              {t('common:action.cancel')}
            </Button>
            <Button
              disabled={!custom}
              onClick={() => {
                if (!custom) return;
                onSnooze(custom);
                setPicking(false);
              }}
            >
              {t('common:action.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
